import { ActionIcon, Group, Progress, Stack } from '@mantine/core';
import { SettingsButton } from '@repo/common/components';
import { SettingsBlock } from '@repo/common/components/SettingsBlock';
import { ResourceIcon, Switch, Text } from '@repo/foundations';
import dayjs from 'dayjs';
import type { ChangeEvent } from 'react';
import { useBudgetSettings } from './hooks';

interface BudgetSettingsControllerProps {
	onSettingsChange?: () => void;
}

export function BudgetSettingsController({
	onSettingsChange,
}: BudgetSettingsControllerProps) {
	const {
		budgetSettings,
		refetchBudgetSettings,
		setBudgetSettings,
		handleEditBudgetSettings,
		handleToggleBudgetEnabled,
		formatCurrency,
		progressPercentage,
		getProgressColor,
	} = useBudgetSettings({ onSettingsChange });

	// Calculate progress percentage
	const monthlyBudget = budgetSettings?.monthly_budget || 0;
	const spendMonthToDate = budgetSettings?.credit_spend_month_to_date || 0;

	return (
		<Stack spacing={0}>
			<SettingsButton
				title="Spend limit"
				description={
					budgetSettings?.enabled
						? `${formatCurrency(monthlyBudget)}`
						: 'Spend limit is disabled'
				}
				leftComponent={<ResourceIcon iconName="creditCard" />}
				onClick={handleEditBudgetSettings}
			>
				<Group spacing="xs">
					{budgetSettings?.enabled && (
						<ActionIcon
							variant="light"
							size="sm"
							onClick={(e: React.MouseEvent) => {
								e.stopPropagation();
								handleEditBudgetSettings();
							}}
						>
							<ResourceIcon iconName="settings" />
						</ActionIcon>
					)}
					<Switch
						checked={budgetSettings?.enabled || false}
						onChange={(event: ChangeEvent<HTMLInputElement>) => {
							handleToggleBudgetEnabled(event.currentTarget.checked);
							if (event.currentTarget.checked) {
								handleEditBudgetSettings();
							}
						}}
					/>
				</Group>
			</SettingsButton>

			{/* Progress bar for monthly spend - shown when enabled */}
			{budgetSettings?.enabled && monthlyBudget > 0 && (
				<SettingsBlock
					rightComponent={
						<Text size="sm" fw={500}>
							{formatCurrency(spendMonthToDate)} /{' '}
							{formatCurrency(monthlyBudget)}
						</Text>
					}
					title="Monthly credit spend"
					description={`Resets on ${dayjs(budgetSettings.reset_date).format('MMM D, YYYY')}`}
				>
					<Progress
						value={Math.min(progressPercentage, 100)}
						color={getProgressColor(progressPercentage)}
						size="sm"
						radius="sm"
					/>
					{progressPercentage > 100 && (
						<Text size="xs" c="critical" mt="xs">
							Spending limit exceeded by{' '}
							{formatCurrency(spendMonthToDate - monthlyBudget)}
						</Text>
					)}
				</SettingsBlock>
			)}
		</Stack>
	);
}
